import type { Entity } from "cesium";

import { HeadingPitchRoll } from "cesium";
import { Math } from "cesium";
import type { Viewer } from "cesium";
import { CAMERA_DRONE } from "./constants";
import { Cartesian3 } from "cesium";
import { HeadingPitchRange } from "cesium";
import { Cartographic } from "cesium";
import { Color } from "cesium";
import type { LabelGraphics } from "cesium";
import { PolylineArrowMaterialProperty } from "cesium";
import { PolylineOutlineMaterialProperty } from "cesium";
import { PolylineDashMaterialProperty } from "cesium";
import { Ellipsoid } from "cesium";
import type { ViewData } from "~/types/cesium";
import { Model } from "cesium";
import { ColorMaterialProperty } from "cesium";

let viewer: Viewer | null = null;
let heightOffset: number = 0;
export const setViewer = (v: Viewer | null) => {
  viewer = v;
};

export const getViewer = () => viewer;
export const getHeightOffset = () => heightOffset;

export const getModelsById = (id: string) => {
  const results: any[] = [];
  if (!viewer || !viewer.scene || !viewer.scene.primitives) {
    console.warn("Viewer or scene is not initialized");
    return results;
  }
  // console.log("getModelsByName", name, viewer.scene.primitives);
  for (let i = 0; i < viewer.scene.primitives.length; i++) {
    const primitive = viewer.scene.primitives.get(i);
    if (primitive._id === id || primitive.name === id) {
      // console.log(`Found model with id: ${id}`);
      results.push(primitive);
    }
  }

  return results;
};
export const getEntityByName = (name: string) =>
  viewer &&
  viewer.entities.values.find((entity: Entity) => entity.name == name);
export const removeMeasurementEntity = (measurementId: string | number) => {
  if (!viewer) return ;
    const entitiesToRemove = viewer!.entities.values.filter((entity: Entity) => {
      // console.log(entity.properties, measurementId);
      return entity.properties && entity.properties.measurementId && entity.properties.measurementId._value == measurementId;
    });
    if (entitiesToRemove.length) {
      entitiesToRemove.forEach(entity => viewer!.entities.remove(entity));
    }
};
export const removeEntityByName = (name: string, startsWith = false) => {

  if (startsWith) {
    if (!viewer) {
      console.warn("Viewer is not initialized");
      return;
    }
    viewer.entities.values.forEach((entity: Entity) => {
      if (entity.name && entity.name.charAt(0) == name)
        if (viewer) {
          viewer.entities.remove(entity);
        }
    });
  } else {
    const entities = getEntitiesByName(name);
    if (!viewer) {
      console.warn("Viewer is not initialized");
      return;
    }
    entities.forEach((entity: Entity) => {
      if (viewer) {
        viewer.entities.remove(entity);
      }
    });
  }
};
export const getEntitiesByName = (name: string) => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return [];
  }
  return viewer.entities.values.filter((entity: Entity) => entity.name == name);
};

export const getEntityByProperty = (
  propertyName: string,
  propertyValue: any
) => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return null;
  }
  // console.log("getEntityByProperty", propertyName, propertyValue);
  // console.log(viewer.entities.values)
  return viewer.entities.values.find((entity: Entity) => {
    return entity.properties && entity.properties[propertyName] && entity.properties[propertyName]._value === propertyValue;
  });
};
export const getEntitiesByProperty = (
  propertyName: string,
  propertyValue: any
) => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return [];
  }
  // console.log("getEntityByProperty", propertyName, propertyValue);
  // console.log(viewer.entities.values)
  return viewer.entities.values.filter((entity: Entity) => {
    return entity.properties && entity.properties[propertyName] && entity.properties[propertyName]._value === propertyValue;
  });
};
export const deleteEntitiesByProperty = (
  propertyName: string,
  propertyValue: any
) => {
  const entities = getEntitiesByProperty(propertyName, propertyValue);
  if (entities && viewer) {
    entities.forEach(entity => viewer && viewer.entities.remove(entity));
  }
};

export type ModelsRefs = Record<string, any[]>;

export const getModelsRefs = (modelNames: string[]): ModelsRefs => {
  // console.log("getModelsRefs", { modelNames });
  const modelsRefs: ModelsRefs = {};
  if (modelNames.length === 0) {
    return modelsRefs;
  }
  modelNames.forEach((modelName) => {
    const models = getModelsById(modelName);
    if (models.length) {
      modelsRefs[modelName] = models;
    }
  });
  return modelsRefs;
};

export const zoomToEntity = (
  duration: number,
  entity?: Entity,
  propertyName?: string,
  propertyValue?: any,
  offsetDistance?: number,
) => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return;
  }

  let targetEntity: Entity | undefined;

  if (entity) {
    targetEntity = entity;
  } else if (propertyName && propertyValue) {
    const foundEntity = getEntityByProperty(propertyName, propertyValue);
    if (!foundEntity) {
      console.warn(
        `Entity with property ${propertyName} and value ${propertyValue} not found`
      );
      return;
    }
    targetEntity = foundEntity;
  }

  if (targetEntity) {
    const position = getEntityPosition(targetEntity);

    if (!position || duration <= 0) {
      viewer.zoomTo(targetEntity);
      return;
    }

    const offset = offsetDistance || 100; // Default 100 meters back
    const currentCameraPosition = viewer.camera.position.clone();
    const cameraPosition = Cartesian3.clone(position);
    cameraPosition.z += offset; // Move camera above the entity

    // Calculate direction from camera to target
    const direction = Cartesian3.subtract(position, cameraPosition, new Cartesian3());
    Cartesian3.normalize(direction, direction);

    // Calculate up vector (use world up as reference)
    const up = Cartesian3.normalize(cameraPosition, new Cartesian3());

    viewer.camera.flyTo({
      destination: cameraPosition,
      orientation: {
        direction: direction,
        up: up
      },
      duration // Animation duration in seconds
    });

    // Option 3: Use current camera orientation (your current approach)
    // const currentDirection = viewer.camera.direction.clone();
    // const currentUp = viewer.camera.up.clone();
    // viewer.camera.flyTo({
    //     destination: position,
    //     orientation: {
    //         direction: currentDirection,
    //         up: currentUp
    //     },
    //     duration: 1
    // });
  }
};

// Alternative method: Use lookAt for better camera positioning
export const flyToEntityLookAt = (
  entity?: Entity,
  propertyName?: string,
  propertyValue?: any,
  offsetDistance?: number
) => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return;
  }

  let targetEntity: Entity | undefined;

  if (entity) {
    targetEntity = entity;
  } else if (propertyName && propertyValue) {
    const foundEntity = getEntityByProperty(propertyName, propertyValue);
    if (!foundEntity) {
      console.warn(
        `Entity with property ${propertyName} and value ${propertyValue} not found`
      );
      return;
    }
    targetEntity = foundEntity;
  }

  if (targetEntity) {
    const position = getEntityPosition(targetEntity);

    if (!position) {
      console.warn("Position not found for entity", targetEntity);
      viewer.zoomTo(targetEntity);
      return;
    }

    const offset = offsetDistance || 100;

    // Calculate camera position with proper spherical coordinates
    const cartographic = viewer.scene.globe.ellipsoid.cartesianToCartographic(position);
    const cameraCartographic = new Cartographic(
      cartographic.longitude,
      cartographic.latitude,
      cartographic.height + offset
    );
    const cameraPosition = viewer.scene.globe.ellipsoid.cartographicToCartesian(cameraCartographic);

    // Use lookAt for proper orientation
    const hpr = new HeadingPitchRange(0, -globalThis.Math.PI / 4, 0);

    viewer.camera.flyTo({
      destination: cameraPosition,
      complete: () => {
        // After flying, look at the target
        if (viewer) {
          viewer.camera.lookAt(position, hpr);
        }
      },
      duration: 2
    });
  }
};

export const flyTo = (vieweData: ViewData) => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return;
  }
  viewer.camera.flyTo({
    destination: vieweData.position,
    orientation: {
      heading: vieweData.heading,
      pitch: vieweData.pitch,
      roll: vieweData.roll,
    },
    duration: 1
    
  
})
}
// Additional utility functions for flying to entities

export const flyToEntityWithBestView = (entity: Entity, distance: number = 100) => {
  if (!viewer || !entity) {
    console.warn("Viewer or entity is not initialized");
    return;
  }

  // Use Cesium's built-in zoomTo for automatic best view
  viewer.zoomTo(entity, new HeadingPitchRange(0, -globalThis.Math.PI / 4, distance));
};

export const flyToEntityFromDirection = (
  entity: Entity,
  heading: number = 0,
  pitch: number = -45,
  distance: number = 100
) => {
  if (!viewer || !entity) {
    console.warn("Viewer or entity is not initialized");
    return;
  }

  const position = getEntityPosition(entity);
  if (!position) {
    console.warn("Position not found for entity");
    // Fallback to viewer.zoomTo
    viewer.zoomTo(entity);
    return;
  }

  // Calculate camera position based on heading, pitch, and distance
  const headingRadians = Math.toRadians(heading);
  const pitchRadians = Math.toRadians(pitch);

  const cameraPosition = new Cartesian3(
    position.x + distance * globalThis.Math.cos(pitchRadians) * globalThis.Math.sin(headingRadians),
    position.y + distance * globalThis.Math.cos(pitchRadians) * globalThis.Math.cos(headingRadians),
    position.z + distance * globalThis.Math.sin(pitchRadians)
  );

  // Calculate direction from camera to target
  const direction = Cartesian3.subtract(position, cameraPosition, new Cartesian3());
  Cartesian3.normalize(direction, direction);

  // Calculate up vector
  const up = Cartesian3.normalize(cameraPosition, new Cartesian3());

  viewer.camera.flyTo({
    destination: cameraPosition,
    orientation: {
      direction: direction,
      up: up
    },
    duration: 2
  });
};

export const flyToEntityByName = (entityName: string, distance: number = 100) => {
  const entity = getEntityByName(entityName);
  if (!entity) {
    console.warn(`Entity with name ${entityName} not found`);
    return;
  }

  flyToEntityWithBestView(entity, distance);
};

// Helper function to get position from any entity type
export const getEntityPosition = (entity: Entity): Cartesian3 | undefined => {
  if (!entity) return undefined;

  // Try direct position first
  if (entity.position) {
    return entity.position.getValue();
  }

  // Handle polylines
  if (entity.polyline && entity.polyline.positions) {
    const positions = entity.polyline.positions.getValue();
    if (positions && positions.length > 0) {
      // Use center point of polyline
      const center = new Cartesian3(0, 0, 0);
      for (const pos of positions) {
        Cartesian3.add(center, pos, center);
      }
      Cartesian3.divideByScalar(center, positions.length, center);
      return center;
    }
  }

  // Handle polygons
  if (entity.polygon && entity.polygon.hierarchy) {
    const hierarchy = entity.polygon.hierarchy.getValue();
    if (hierarchy && hierarchy.positions && hierarchy.positions.length > 0) {
      // Use center point of polygon
      const center = new Cartesian3(0, 0, 0);
      for (const pos of hierarchy.positions) {
        Cartesian3.add(center, pos, center);
      }
      Cartesian3.divideByScalar(center, hierarchy.positions.length, center);
      return center;
    }
  }

  // Handle rectangles
  if (entity.rectangle && entity.rectangle.coordinates) {
    const coords = entity.rectangle.coordinates.getValue();
    if (coords) {
      // Calculate center of rectangle
      const centerLon = (coords.west + coords.east) / 2;
      const centerLat = (coords.north + coords.south) / 2;
      return Cartesian3.fromRadians(centerLon, centerLat, 0);
    }
  }

  // Handle corridors
  if (entity.corridor && entity.corridor.positions) {
    const positions = entity.corridor.positions.getValue();
    if (positions && positions.length > 0) {
      // Use center point of corridor
      const center = new Cartesian3(0, 0, 0);
      for (const pos of positions) {
        Cartesian3.add(center, pos, center);
      }
      Cartesian3.divideByScalar(center, positions.length, center);
      return center;
    }
  }

  return undefined;
};

export const addCesiumEntity = (
  position: Cartesian3,
  name: string = "",
  obj: Record<string, any>,
  show: boolean = true,
  properties?: Record<string, any>
): Entity => {
  if (!viewer) {
    throw new Error("Viewer is not initialized");
  }
  return viewer.entities.add({
    name,
    show,
    position,
    ...obj,
    properties
  });

};
export type TAddLineEntityParams = {
  positions: Cartesian3[];
  arrow?: boolean;
  color?: Color;
  name?: string;
  show?: boolean;
  properties?: Record<string, any>;
  width?: number;
  label?: LabelGraphics;
  position?: Cartesian3;
  id?: number ;
}
export const addLineEntity = ({
  positions,
  arrow = false,
  color,
  name = '',
  show = true,
  properties,
  width = 3,
  label,
  position,
}: TAddLineEntityParams): Entity | null => {
  if (!viewer) {
    return null;
  }
  return viewer.entities.add({
    name,
    show,
    polyline: {
      positions,
      width,
      material: arrow
        ? new PolylineArrowMaterialProperty(
          color ? color : Color.CHARTREUSE,
        )
        : new PolylineOutlineMaterialProperty({
          color: color || Color.ORANGE,
        }),
      depthFailMaterial: new PolylineDashMaterialProperty({
        color: color || (arrow ? Color.CHARTREUSE : Color.RED),
      }),

    },
    properties,
    position,
    label,
  });
}

export const addModelToViewer = async (modelUrl: string): Promise<{ name: string }[]> => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return [];
  }

  const model = await Model.fromGltfAsync({
    url: modelUrl,
  });
  
  // Add model to viewer
  viewer.scene.primitives.add(model);
  
  // Wait for the model to be ready and return the nodes
  return new Promise((resolve) => {
    model.readyEvent.addEventListener(() => {
      const nodes = model._nodesByName;
      const nodesData = Object.keys(nodes).map((nodeName, index) => {
        const node = nodes[nodeName];

        if (node.id !== 0) node.show = false;
        return {
          // bid: props.bridge.bid,
          // span_id: null,
          // bridge_model_id: props.model.id,
          // object_id: nodeName,
          // boundingSphere: nodeBoudingSphere,
          name: nodeName,
        };
      });
      resolve(nodesData);
    });
  });
};

export const getCurrentViewData = () => {
  if (!viewer) {
    console.warn("Viewer is not initialized");
    return null;
  }
  const cam = viewer.camera;
  const { heading } = cam;
  const { pitch } = cam;
  const { roll } = cam;
  const { position } = cam;
  return { heading, pitch, roll, position };
  // return computeViewRectangle
};

export const setPolygonsTransparency = (transparency: number) => {
  if (viewer) {
      viewer.entities.values.forEach(entity => {
        if (entity.polygon && entity.polygon.material) {
          let color = Color.WHITE;
          const material = entity.polygon.material as any;
          if (material.color) {
            color = material.color.getValue();
          }
          // Create a new color with the updated alpha
          const newColor = new Color(
            color.red,
            color.green,
            color.blue,
            transparency
          );
          // Assign a new ColorMaterialProperty
          entity.polygon.material = new ColorMaterialProperty(newColor);
        }
      });
    }
}